// src/components/Layout/Navbar.tsx - Fixed flyout menu
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Settings, Map, Star, Home, Trash } from 'lucide-react';
import { authService } from '../../services/AuthService';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  
  // Direct localStorage check
  const userEmail = localStorage.getItem('tripperUserEmail');
  
  // Get user data from localStorage
  const getUserData = () => {
    try {
      const data = localStorage.getItem('userData');
      return data ? JSON.parse(data) : { name: '', email: userEmail };
    } catch (e) {
      return { name: '', email: userEmail };
    }
  };
  
  const user = getUserData();

  const toggleMenu = () => {
    console.log('Toggle menu clicked, current state:', isMenuOpen);
    setIsMenuOpen(prevState => !prevState);
  };

  // Debug when state changes
  useEffect(() => {
    console.log('Menu state changed to:', isMenuOpen);
  }, [isMenuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsMenuOpen(false);
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      return false;
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This is irreversible.')) {
      try {
        const result = await authService.deleteAccount();
        
        if (result.success) {
          setTimeout(() => {
            localStorage.setItem('forceRerender', Date.now().toString());
          }, 500);
        }
      } catch (error) {
        console.error('Error during account deletion:', error);
        alert('Account deletion failed. See console for details.');
      }
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* App Title - Top Left */}
      {/* <div className="fixed top-4 left-4 z-[9999]">
        <span className="text-blue-600 text-xl font-bold">Tripper</span>
      </div> */}

      {/* Compact Burger Menu Button - Always visible */}
      <div className="fixed top-4 right-4 z-[10000]">
        <button
          type="button"
          className="p-2 rounded-full bg-white shadow-md hover:bg-gray-100 focus:outline-none"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          <div className="w-6 h-6 relative">
            <div 
              className="transition-all duration-300 ease-in-out"
              style={{ 
                transform: `rotate(${isMenuOpen ? -90 : 0}deg)`
              }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </div>
          </div>
        </button>
      </div>

      {/* Flyout Menu - Absolutely positioned with higher z-index */}
        <div 
          ref={menuRef}
          className={`fixed top-14 right-4 z-[9999] w-64 bg-white rounded-lg shadow-xl border border-gray-200 transition-all duration-300 transform origin-top-right ${
            isMenuOpen 
              ? 'opacity-100 scale-100 translate-y-0' 
              : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
          }`}
        >
        {/* User Profile Section */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase() || <User size={20} />}
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <div className="text-base font-medium text-gray-800">{user?.name || 'User'}</div>
              <div className="text-sm text-gray-500 truncate max-w-[180px]">{user?.email || ''}</div>
            </div>
          </div>
        </div>
        {/* Navigation Links */}
        <div className="py-1">
          {/* <a href="/"
            className={`block px-4 py-2 text-sm ${
              isActive('/') 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex items-center">
              <Home size={18} className="mr-2" />
              Home
            </div>
          </a>
          
          <a href="/explore"
            className={`block px-4 py-2 text-sm ${
              isActive('/explore') 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex items-center">
              <Map size={18} className="mr-2" />
              Explore
            </div>
          </a> */}
          
          {/* <a href="/favorites"
            className={`block px-4 py-2 text-sm ${
              isActive('/favorites') 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex items-center">
              <Star size={18} className="mr-2" />
              Favorites
            </div>
          </a> */}
        </div>
        
        {/* Divider */}
        {/* <div className="border-t border-gray-200"></div> */}
        
        {/* User Actions */}
        <div className="py-1">
          {/* <a href="/profile"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex items-center">
              <User size={18} className="mr-2" />
              Your Profile
            </div>
          </a>
          
          <a href="/settings"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex items-center">
              <Settings size={18} className="mr-2" />
              Settings
            </div>
          </a> */}
          
          {user?.role === 'admin' && (
            <a href="/admin"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center">
                <Settings size={18} className="mr-2" />
                Admin Panel
              </div>
            </a>
          )}
          
          {/* <button
            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            onClick={handleDeleteAccount}
          >
            <div className="flex items-center">
              <Trash size={18} className="mr-2" />
              Delete Account (Testing)
            </div>
          </button> */}
          
          <button
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={handleLogout}
          >
            <div className="flex items-center">
              <LogOut size={18} className="mr-2" />
              Sign out
            </div>
          </button>
        </div>
      </div>
    </>
  );
}